import {
  VirtualSearchAfter,
  VirtualSearchCallback,
  VirtualSearchPayload,
  VirtualSearchResponse,
} from '@/types/virtual-search';
import { useState } from '@/composables/common/useState';
import { computed, ComputedRef, Ref, ref, unref, watch } from 'vue';
import { useToggle } from '@/composables/useToggle';
import { MaybeRef } from '@/types/utils';
import { diff } from 'deep-object-diff';

export interface VirtualSearchOptions {
  enabled?: MaybeRef<boolean>;
  immediate?: MaybeRef<boolean>;
  diffRefetch?: boolean;
}

export const useVirtualSearch = <T>(
  fn: VirtualSearchCallback<T>,
  filters: ComputedRef<VirtualSearchPayload<T>> | Ref<VirtualSearchPayload<T>>,
  options: VirtualSearchOptions = { enabled: true }
) => {
  const [response, setResponse] = useState<VirtualSearchResponse<T> | null>(null);
  const total = ref<number | null>(null);
  const pit = ref<string | null>(null);
  const [isFetching, setIsFetching] = useToggle(false);
  const [nextQuery, setNextQuery] = useState<VirtualSearchAfter | null>(null);
  const [items, setItems] = useState<T[]>([]);
  const [aggregations, setAggregations] = useState<VirtualSearchResponse<T>['aggregations'] | null>(
    null
  );

  const fetchedAllItems = computed(() => total.value === items.value.length);

  const search = async (next: boolean = false) => {
    if (!unref(options.enabled)) return;
    try {
      setIsFetching(true);

      const { data } = await fn({
        ...filters.value,
        ...(next && nextQuery.value
          ? { searchAfter: nextQuery.value, pit: pit.value as string }
          : {}),
      });

      setResponse(data);
      const { items: fetchedItems, aggregations: fetchedAggregations } = data;

      total.value = data.total;
      pit.value = data.pit;
      setNextQuery(fetchedItems[fetchedItems.length - 1]?.sort);

      const currentItems = [
        ...(next ? items.value : []),
        ...fetchedItems.map((item) => ({ id: item._id, _id: item._id, ...item._source })),
      ];

      setItems(currentItems);
      setAggregations(fetchedAggregations);
    } finally {
      setIsFetching(false);
    }
  };

  watch(
    filters,
    async (value, oldValue) => {
      if (options.diffRefetch && !Object.keys(diff(oldValue || {}, value)).length) return;
      await search();
    },
    { deep: true, immediate: unref(options.immediate) }
  );

  const refetch = () => {
    search(false);
  };

  return { isFetching, aggregations, items, fetchedAllItems, response, total, search, refetch };
};
